type GetSelectedCountArgs = {
  checked: boolean;
  useSelectAllMode: boolean;
  isAiHistory: boolean;
  currentContextCount?: number | null;
  selectionScopeCount?: number | null;
  totalShipments?: number | null;
  overAllShipmentCount?: number | null;
  excludedIdsLength: number;
  selectedIdsLength: number;
};

type GetShipmentToolbarCountTextArgs = {
  checked?: boolean;
  selectedCountText?: string;
  totalShipments?: number | null;
  appliedFilters?: unknown[] | null;
  isSearchTriggered?: boolean;
  selected?: unknown[] | null;
  activeTabId?: string;
  getLabel: (count: number) => string;
  includeFilteredEntityLabel?: boolean;
  showActiveQualifier?: boolean;
};

type CalendarScopedFilters = {
  filters: Record<string, unknown>;
  start?: string;
  end?: string;
  view_by?: string;
};

// Calendar's select-all needs the visible date range sent to the backend
// as top-level start/end/view_by (sibling to select_all/filters), not
// nested inside `filters` where the resolver won't see them. List view
// never sets these keys, so they're simply absent here for List.
export const splitCalendarScopedFilters = (
  filtersPayload?: Record<string, unknown> | null
): CalendarScopedFilters => {
  const source: Record<string, unknown> =
    filtersPayload && typeof filtersPayload === 'object' ? filtersPayload : {};
  const { start, end, view_by, ...filters } = source as Record<
    string,
    unknown
  > & {
    start?: string;
    end?: string;
    view_by?: string;
  };
  return {
    filters,
    ...(typeof start === 'string' && start ? { start } : {}),
    ...(typeof end === 'string' && end ? { end } : {}),
    ...(typeof view_by === 'string' && view_by ? { view_by } : {}),
  };
};

export const getToolbarSelectedCount = ({
  checked,
  useSelectAllMode,
  isAiHistory,
  currentContextCount,
  selectionScopeCount,
  totalShipments,
  overAllShipmentCount,
  excludedIdsLength,
  selectedIdsLength,
}: GetSelectedCountArgs): number => {
  if (!checked) return 0;
  if (!useSelectAllMode) return selectedIdsLength;

  if (isAiHistory) {
    return Math.max(0, (totalShipments ?? 0) - excludedIdsLength);
  }

  const baseCount =
    typeof selectionScopeCount === 'number'
      ? selectionScopeCount
      : (overAllShipmentCount ?? totalShipments ?? 0);
  const shouldUseContextFallback =
    (selectionScopeCount == null || selectionScopeCount === 0) &&
    baseCount === 0 &&
    (currentContextCount ?? 0) > 0;
  const effectiveBaseCount = shouldUseContextFallback
    ? (currentContextCount ?? 0)
    : baseCount;

  return Math.max(0, effectiveBaseCount - excludedIdsLength);
};

export const getShipmentToolbarCountText = ({
  checked = false,
  selectedCountText = '',
  totalShipments,
  appliedFilters,
  isSearchTriggered = false,
  selected,
  activeTabId = '',
  getLabel,
  includeFilteredEntityLabel = false,
  showActiveQualifier = false,
}: GetShipmentToolbarCountTextArgs): string => {
  if (checked) return selectedCountText;

  const count = totalShipments ?? 0;
  const hasScopedResults = Boolean(
    appliedFilters?.length || isSearchTriggered || selected?.length
  );

  if (hasScopedResults) {
    return `${count} filtered${
      includeFilteredEntityLabel ? ` ${getLabel(count)}` : ''
    }`;
  }

  const activeQualifier =
    showActiveQualifier && activeTabId !== 'all_shipments' ? ' active' : '';

  return `${count}${activeQualifier} ${getLabel(count)}`;
};
